import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from 'ui/styled'

import {
  ActionButton,
  CardRoot,
  HeaderMeta,
  HeaderBlock,
  HeaderTitle,
  LessonButton,
  LessonIcon,
  LessonMeta,
  LessonMetaDot,
  LessonMetaHighlight,
  LessonText,
  LessonTitle,
  ListScroll,
  ModuleGroup,
  ModuleHeader,
  ModuleTime,
  RightActions,
} from './styled'

function TruncatedLessonTitle({ children }) {
  const titleRef = useRef(null)
  const [tooltip, setTooltip] = useState(undefined)

  const checkIsTruncated = useCallback(() => {
    const element = titleRef.current
    if (!element) return

    const isTruncated = element.scrollHeight > element.clientHeight + 1
      || element.scrollWidth > element.clientWidth + 1

    setTooltip(isTruncated ? children : undefined)
  }, [children])

  useEffect(() => {
    checkIsTruncated()

    const element = titleRef.current
    if (!element) return undefined

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', checkIsTruncated)
      return () => window.removeEventListener('resize', checkIsTruncated)
    }

    const observer = new ResizeObserver(() => checkIsTruncated())
    observer.observe(element)

    return () => observer.disconnect()
  }, [checkIsTruncated])

  return (
    <LessonTitle ref={titleRef} title={tooltip}>
      {children}
    </LessonTitle>
  )
}

export default function CourseDetailsModuleList({
  title = 'Course Modules',
  subtitle,
  modules,
  selectedLessonId,
  onSelectLesson,
  onToggleFavorite,
}) {
  return (
    <CardRoot>
      <HeaderBlock>
        <HeaderTitle>{title}</HeaderTitle>
        {!subtitle ? null : <HeaderMeta>{subtitle}</HeaderMeta>}
      </HeaderBlock>

      <ListScroll>
        {(modules || []).map(module => (
          <ModuleGroup key={module?.id}>
            <ModuleHeader>
              <div>{module?.title}</div>
              {!module?.time ? null : <ModuleTime>{module?.time}</ModuleTime>}
            </ModuleHeader>

            {(module?.lessons || []).map(lesson => (
              <LessonButton
                key={lesson?.id}
                active={lesson?.id === selectedLessonId}
                onClick={() => onSelectLesson?.(lesson)}
                type="button"
              >
                <LessonIcon active={lesson?.id === selectedLessonId}>
                  <Icon icon={lesson?.type || 'article'} />
                </LessonIcon>

                <LessonText>
                  <TruncatedLessonTitle>{lesson?.title}</TruncatedLessonTitle>
                  <LessonMeta>
                    <span>{lesson?.time || module?.time || '--:--'}</span>
                    {lesson?.isCompleted ? (
                      <>
                        <LessonMetaDot />
                        <LessonMetaHighlight>Completed</LessonMetaHighlight>
                      </>
                    ) : null}
                  </LessonMeta>
                </LessonText>

                <RightActions>
                  <ActionButton
                    onClick={event => {
                      event.stopPropagation()
                      onToggleFavorite?.(module?.id, lesson)
                    }}
                    type="button"
                  >
                    <Icon icon={lesson?.isFavorite ? 'heart-active' : 'heart'} pointer />
                  </ActionButton>

                  <Icon icon={lesson?.isCompleted ? 'circle-active' : 'circle'} pointer />
                </RightActions>
              </LessonButton>
            ))}
          </ModuleGroup>
        ))}
      </ListScroll>
    </CardRoot>
  )
}
